import {CaretRightOutlined, CheckOutlined} from '@ant-design/icons';
import {Button, Checkbox, Collapse, InputNumber, Radio, Slider} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import {LuFilter} from 'react-icons/lu';

import {RQGetBrandAPI, RQGetCategoryAPI, RqGetAllProducts} from '../../../reactQuery/apis';

const {Panel} = Collapse;

const ProductFilters = ({filters, setFilters, products = []}) => {
  const {data: categories = []} = RQGetCategoryAPI();
  const {data: brandsResponse = []} = RQGetBrandAPI();

  const brandsData = brandsResponse?.data || brandsResponse || [];

  /* BRAND OPTIONS */
  const brands = useMemo(() => {
    if (!Array.isArray(brandsData)) {return [];}
    return brandsData
      .filter(b => b.isActive)
      .map(brand => ({value: brand.name, label: brand.name}));
  }, [brandsData]);

  /* CATEGORY OPTIONS */
  const productCategories = useMemo(() => {
    if (!Array.isArray(categories)) {return [];}
    return categories
      .filter(cat => cat.isActive)
      .map(cat => ({value: cat.name, label: cat.name}));
  }, [categories]);

  /* FEATURES OPTIONS */
  const featureOptions = useMemo(() => {
    if (!Array.isArray(categories)) {return [];}
    const activeCategories = filters.productCategories.length
      ? categories.filter(cat => filters.productCategories.includes(cat.name))
      : categories;
    const features = activeCategories
      .filter(cat => cat.isActive)
      .flatMap(cat => (cat.features?.features || []));
    return [...new Set(features)].map(f => ({label: f, value: f}));
  }, [categories, filters.productCategories]);

  /* COLOR OPTIONS */
  const colorOptions = useMemo(() => {
    if (!Array.isArray(products)) {return [];}
    return Array.from(
      new Set(
        products.flatMap(p => {
          if (!Array.isArray(p.variants)) {return [];}
          return p.variants.map(v => v.color).filter(Boolean);
        }),
      ),
    );
  }, [products]);

  const [activeKey, setActiveKey] = useState(['1']);

  /* PRICE STATE */
  const [priceFilters, setPriceFilters] = useState({minPrice: '', maxPrice: ''});

  useEffect(() => {
    setPriceFilters({minPrice: filters.minPrice || '', maxPrice: filters.maxPrice || ''});
  }, [filters.minPrice, filters.maxPrice]);

  const handlePriceUIChange = (type, value) => {
    setPriceFilters(prev => ({...prev, [type]: value}));
  };

  const applyPriceFilters = (min, max) => {
    setFilters(prev => ({...prev, minPrice: min, maxPrice: max}));
  };

  /* CLEAR ALL */
  const DEFAULT_FILTERS = {
    productCategories: [],
    brands: [],
    colors: [],
    features: [],
    minPrice: '',
    maxPrice: '',
  };

  const hasActiveFilters =
    filters.productCategories.length > 0 ||
    filters.brands.length > 0 ||
    filters.features.length > 0 ||
    filters.colors.length > 0 ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '';

  return (
    <div>
      <h1 className={'font-semibold text-xl md:flex items-center gap-1 hidden'}>
        <LuFilter /> Filters
      </h1>

      <div className={'overflow-y-auto'}>
        <div className={'flex justify-end mb-2'}>
          {hasActiveFilters && (
            <Button
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className={'font-semibold text-primary-900 border-none'}
            >
              Clear All
            </Button>
          )}
        </div>

        <Collapse
          className={'w-full bg-white border-none'}
          expandIcon={({isActive}) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          expandIconPosition={'end'}
          activeKey={activeKey}
          onChange={setActiveKey}
        >
          {/* BRAND FILTER */}
          <Panel key={'1'} header={<div className={'font-medium lg:text-base'}>Brands</div>}>
            <Button
              type={'link'}
              className={'p-0 mb-2 font-semibold'}
              onClick={() => setFilters(prev => ({...prev, brands: []}))}
            >
              Reset
            </Button>

            <Checkbox.Group
              options={brands}
              value={filters.brands}
              onChange={e => setFilters(prev => ({...prev, brands: e}))}
              className={'flex flex-col gap-2'}
            />
          </Panel>

          {/* CATEGORY FILTER */}
          <Panel key={'2'} header={<div className={'font-medium lg:text-base'}>Categories</div>}>
            <Button
              type={'link'}
              className={'p-0 mb-2 font-semibold'}
              onClick={() => setFilters(prev => ({...prev, productCategories: []}))}
            >
    Reset
            </Button>

            <Checkbox.Group
              options={productCategories}
              value={filters.productCategories}               
              onChange={checkedValues =>
                setFilters(prev => ({
                  ...prev,
                  productCategories: checkedValues,            
                }))
              }
              className={'flex flex-col gap-2'}
            />
          </Panel>

          {/* FEATURES FILTER */}
          <Panel key={'5'} header={<div className={'font-medium lg:text-base'}>Features</div>}>
            <Button
              type={'link'}
              className={'p-0 mb-2 font-semibold'}
              onClick={() => setFilters(prev => ({...prev, features: []}))}
            >
              Reset
            </Button>

            <Checkbox.Group
              options={featureOptions}
              value={filters.features || []}
              onChange={checkedValues =>
                setFilters(prev => ({
                  ...prev,
                  features: checkedValues,
                }))
              }
              className={'flex flex-col gap-2'}
            />
          </Panel>

          {/* PRICE FILTER */}
          <Panel key={'3'} header={<div className={'font-medium lg:text-base'}>Price</div>}>
            <Button
              type={'link'}
              className={'p-0 mb-2 font-semibold'}
              onClick={() => {
                setPriceFilters({minPrice: '', maxPrice: ''});
                setFilters(prev => ({...prev, minPrice: '', maxPrice: ''}));
              }}
            >
              Reset
            </Button>

            <Slider
              range
              min={0}
              max={100000}
              value={[priceFilters.minPrice || 0, priceFilters.maxPrice || 0]}
              onChange={([min, max]) => {
                handlePriceUIChange('minPrice', min);
                handlePriceUIChange('maxPrice', max);
              }}
              onAfterChange={([min, max]) => applyPriceFilters(min, max)}
            />

            <div className={'flex gap-2 mt-4'}>
              <InputNumber
                className={'w-full'}
                placeholder={'Min Price'}
                value={priceFilters.minPrice}
                onChange={v => handlePriceUIChange('minPrice', v)}
                onBlur={() => applyPriceFilters(priceFilters.minPrice, priceFilters.maxPrice)}
              />
              <InputNumber
                className={'w-full'}
                placeholder={'Max Price'}
                value={priceFilters.maxPrice}
                onChange={v => handlePriceUIChange('maxPrice', v)}
                onBlur={() => applyPriceFilters(priceFilters.minPrice, priceFilters.maxPrice)}
              />
            </div>
          </Panel>

          {/* COLOR FILTER */}
          {/* COLOR FILTER */}
          <Panel key={'4'} header={<div className={'font-medium lg:text-base'}>Colors</div>}>
            <Button
              type={'link'}
              className={'p-0 mb-3 font-semibold'}
              onClick={() => setFilters(prev => ({...prev, colors: []}))}
            >
    Reset
            </Button>

            <div className={'flex flex-wrap gap-2'}>
              {colorOptions.map(color => {
                const normalizedColor = color.toLowerCase();
                const isSelected = filters.colors.includes(color);

                const handleColorClick = () => {
                  setFilters(prev => {
                    const alreadySelected = prev.colors.includes(color);

                    return {
                      ...prev,
                      colors: alreadySelected
                        ? prev.colors.filter(c => c !== color) // remove
                        : [...prev.colors, color], // add
                    };
                  });
                };

                return (
                  <Button
                    key={color}
                    shape={'square'}
                    onClick={handleColorClick}
                    className={`!w-6 !h-6 !p-0 !border-2 ${
                      isSelected ? '!border-gray-900' : '!border-gray-300'
                    }`}
                    style={{backgroundColor: normalizedColor}}
                  >
                    {isSelected && (
                      <CheckOutlined
                        style={{
                          color: normalizedColor === 'white' ? '#000' : '#fff',
                          fontSize: 16,
                        }}
                      />
                    )}
                  </Button>
                );
              })}
            </div>
          </Panel>

        </Collapse>
      </div>
    </div>
  );
};

ProductFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
};

export default ProductFilters;
